import { useRef, useState } from 'preact/hooks';
import Stepper from '../../shared/Stepper/Stepper';
import Step from '../../shared/Stepper/Step';
import AppBar from '../../shared/AppBar/AppBar';
import useAndroidBackPress from '../../../hooks/back.listener.hook';
import { TrackingEvent } from '../../../sdk/tracking.event';
import HomeAddressStep from '../FormSteps/HomeAddressStep';
import ResidentialStatusStep from '../FormSteps/ResidentialStatusStep';
import WorkAddressStep from '../FormSteps/WorkAddressStep';
import EmployerStep from '../FormSteps/EmployerStep';
import MonthlyIncomeStep from '../FormSteps/MonthlyIncomeStep';
import RequestedCreditLimitStep from '../FormSteps/RequestedCreditLimitStep';
import CurrentJobStep from '../FormSteps/CurrentJobStep';
import FamilyDetailsStep from '../FormSteps/FamilyDetailsStep';
import useTranslation from '../../../localization/i18n.context';

const STEP_EVENTS = [
  HomeAddressStep.EventName,
  ResidentialStatusStep.EventName,
  CurrentJobStep.EventName,
  EmployerStep.EventName,
  WorkAddressStep.EventName,
  MonthlyIncomeStep.EventName,
  FamilyDetailsStep.EventName,
  RequestedCreditLimitStep.EventName,
];

export default function CreditOnboardingEgyptLuckyV1({
  onCloseClick,
  onBackClick,
  onFormSubmit,
  pageConfig,
  organization,
}) {
  const [activeStep, setActiveStep] = useState(1);
  const [formData, setFormData] = useState({});

  const backRef = useRef(null);
  const { t, getOrganization } = useTranslation();

  const handleStepChange = e => {
    e.preventDefault();
    const stepEvent = STEP_EVENTS[activeStep - 1];
    TrackingEvent.trigger(
      TrackingEvent.Events.ONBOARDING_CONTINUE_BUTTON_TAP,
      TrackingEvent.Sources.CAAS,
      {
        step: stepEvent,
      },
    );
    setActiveStep(s => s + 1);
  };

  const handleInputChange = e => {
    setFormData(prevState => {
      const newState = { ...prevState };

      const currentKey = newState[e.target.name];

      if (!currentKey) {
        newState[e.target.name] = { value: e.target.value.trimStart() };
      } else {
        newState[e.target.name].value = e.target.value.trimStart();
      }

      return newState;
    });
  };

  const handleFormSubmit = e => {
    e.preventDefault();
    TrackingEvent.trigger(
      TrackingEvent.Events.CREDIT_ONBOARDING_CONTINUE_BUTTON_TAP,
      TrackingEvent.Sources.CAAS,
    );

    onFormSubmit(formData);
  };

  const handleBackClick = () => {
    const stepEvent = STEP_EVENTS[activeStep - 1];
    TrackingEvent.trigger(
      TrackingEvent.Events.ONBOARDING_BACK_TAP,
      TrackingEvent.Sources.CAAS,
      {
        step: stepEvent,
      },
    );
    if (activeStep > 1) {
      setActiveStep(s => s - 1);
    } else {
      onBackClick();
    }
  };

  useAndroidBackPress(backRef.current, () => {
    if (window.location.search.includes('popup')) {
      history.back();
    } else {
      backRef.current.props.onClick();
    }
  });

  return (
    <>
      <AppBar
        onCloseClick={onCloseClick}
        onBackClick={handleBackClick}
        backRef={backRef}
        className={`fixed-bar`}
        title={t('Apply for {company_name} credit', {
          '{company_name}': getOrganization(organization),
        })}
      />
      <form onSubmit={handleFormSubmit}>
        <Stepper activeStep={activeStep}>
          <Step>
            <HomeAddressStep
              onInputChange={handleInputChange}
              onButtonClick={handleStepChange}
              data={formData}
              cities={pageConfig.cities}
              stepNumber={1}
              stepCount={STEP_EVENTS.length}
            />
          </Step>
          <Step>
            <ResidentialStatusStep
              onInputChange={handleInputChange}
              onButtonClick={handleStepChange}
              data={formData}
              types={pageConfig.residentialTypes}
              stepNumber={2}
              stepCount={STEP_EVENTS.length}
            />
          </Step>
          <Step>
            <CurrentJobStep
              onInputChange={handleInputChange}
              onButtonClick={handleStepChange}
              data={formData}
              employmentStatuses={pageConfig.employmentStatus}
              jobSectors={pageConfig.jobSector}
              stepNumber={3}
              stepCount={STEP_EVENTS.length}
              jobAntiquities={pageConfig.jobAntiquity}
            />
          </Step>
          <Step>
            <EmployerStep
              onInputChange={handleInputChange}
              onButtonClick={handleStepChange}
              data={formData}
              stepNumber={4}
              stepCount={STEP_EVENTS.length}
            />
          </Step>
          <Step>
            <WorkAddressStep
              onInputChange={handleInputChange}
              onButtonClick={handleStepChange}
              data={formData}
              cities={pageConfig.cities}
              stepNumber={5}
              stepCount={STEP_EVENTS.length}
            />
          </Step>
          <Step>
            <MonthlyIncomeStep
              onInputChange={handleInputChange}
              onButtonClick={handleStepChange}
              data={formData}
              stepNumber={6}
              stepCount={STEP_EVENTS.length}
            />
          </Step>
          <Step>
            <FamilyDetailsStep
              onInputChange={handleInputChange}
              onButtonClick={handleStepChange}
              data={formData}
              maritalStatuses={pageConfig.maritalStatus}
              familyMembers={pageConfig.familyMembers}
              stepNumber={7}
              stepCount={STEP_EVENTS.length}
            />
          </Step>
          <Step>
            <RequestedCreditLimitStep
              onInputChange={handleInputChange}
              data={formData}
              stepNumber={8}
              stepCount={STEP_EVENTS.length}
            />
          </Step>
        </Stepper>
      </form>
    </>
  );
}
